import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { SelectionProvider } from '../../contexts/SelectionContext';
import { useSelection } from '../../hooks/useSelection';
import { useTranslation } from 'react-i18next';

/**
 * CardCollectionProvider - Manages selection state and provides context for cards
 * 
 * This component:
 * 1. Manages selection state using useSelection hook
 * 2. Provides selection context to all child cards
 * 3. Handles both single and bulk status changes
 * 4. Wraps children with selection context
 */
const CardCollectionProvider = ({ 
  children,
  onStatusChange,          // Single handler for both single and bulk operations
  onDataRefresh,          // Called after successful operations
  onOperationSuccess,     // Success notification callback
  onOperationError,       // Error notification callback
  clientId
}) => {
  const { t } = useTranslation();
  const selection = useSelection();
  
  // Handle status change for single item
  const handleSingleStatusChange = useCallback(async (itemId, newStatus) => {
    try {
      const result = await onStatusChange([itemId], newStatus, false);
      if (result.success) {
        onOperationSuccess?.(t('experimentMovedToStatus', { status: newStatus }));
        await onDataRefresh?.();
      }
    } catch (error) {
      onOperationError?.(t('failedToUpdateExperiment'));
    }
  }, [onStatusChange, onDataRefresh, onOperationSuccess, onOperationError, t]);
  
  // Handle status change for multiple items
  const handleBulkStatusChange = useCallback(async (itemIds, newStatus) => {
    try {
      
      // Always pass all IDs at once - onStatusChange already handles looping
      const result = await onStatusChange(itemIds, newStatus, true);
      
      // If it returns dialog reason, the modal will handle it
      if (result && result.reason === 'dialog') {
        return;
      }
      
      // Handle results
      if (result && result.results) {
        const succeeded = result.results.filter(r => r.success).length;
        const failed = result.results.filter(r => !r.success).length;
        
        if (succeeded > 0) {
          onOperationSuccess?.(t('experimentsMovedToStatus', { 
            count: succeeded, 
            status: newStatus 
          }));
        }
        
        if (failed > 0) {
          onOperationError?.(t('someExperimentsFailed', { count: failed }));
        }
      } else if (result && result.success) {
        onOperationSuccess?.(t('experimentsMovedToStatus', { 
          count: itemIds.length, 
          status: newStatus 
        }));
      }
      
      // Refresh data once after all operations
      await onDataRefresh?.();
      // Clear selection after bulk operation
      selection.clearSelection();
    } catch (error) {
      onOperationError?.(t('bulkOperationFailed'));
    }
  }, [onStatusChange, onDataRefresh, onOperationSuccess, onOperationError, selection, t]);
  
  // Create context value
  const contextValue = {
    ...selection,
    onSingleStatusChange: handleSingleStatusChange,
    onBulkStatusChange: handleBulkStatusChange,
    onDataRefresh,
    clientId
  };
  
  return (
    <SelectionProvider value={contextValue}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '12px' 
      }}>
        {children}
      </Box>
    </SelectionProvider>
  );
};

export default CardCollectionProvider;